import { Fragment } from 'react';
import { FiChevronRight } from 'react-icons/fi';
import { PedeAcao } from './Alertas';
import { Box, Cartao, Explicacao, Flex, Grade, LinhaDoPeriodo } from './base';
import { CLASSES, plural, quantidade, reais, reaisCurtos } from './formato';
import { Alerta, PedidosEmAberto, SituacaoDeHoje } from './tipos';

// A aba "Pendências", em texto: o que pede ação e os pedidos em aberto do
// período escolhido. A aba "Hoje" saiu em 04/11/2026 (o dono: para ver o dia,
// a mesma data nas duas caixas do período). A explicação fica na dica (ⓘ) e o
// detalhe, na gaveta do alerta.

// O título de um cartão da coluna da direita, com a dica.
function TituloDoCartao({ titulo, explicacao, extra }: { titulo: string; explicacao?: string; extra?: React.ReactNode }) {
  return (
    <Flex align="center" justify="space-between" sx={{ gap: '8px' }} px="16px" pt="14px" pb="10px">
      <Flex as="h3" m={0} align="center" sx={{ gap: '2px' }} fontSize="16px" lineHeight="22px" fontWeight={700}>
        {titulo}
        {explicacao && <Explicacao texto={explicacao} />}
      </Flex>
      {extra}
    </Flex>
  );
}

// Uma linha clicável quando há alerta ligado a ela; senão, só leitura.
function Linha({ alerta, onAbrir, title, children }: { alerta?: Alerta; onAbrir: (a: Alerta) => void; title?: string; children: React.ReactNode }) {
  return (
    <Box
      as={alerta ? 'button' : 'div'}
      type={alerta ? 'button' : undefined}
      onClick={alerta ? () => onAbrir(alerta) : undefined}
      title={title}
      display="flex"
      alignItems="center"
      sx={{ gap: '12px' }}
      w="100%"
      textAlign="left"
      px="16px"
      py="11px"
      bg="transparent"
      border="none"
      borderTop="1px solid var(--pa-grade)"
      color="var(--pa-tinta)"
      cursor={alerta ? 'pointer' : 'default'}
      _hover={alerta ? { bg: 'var(--pa-superficie-2)' } : undefined}
      _focusVisible={{ outline: '2px solid var(--pa-dado)', outlineOffset: '-2px' }}
    >
      {children}
      <Box as="span" w="16px" flexShrink={0} color="var(--pa-tinta-3)" aria-hidden="true">
        {alerta && <FiChevronRight size={16} />}
      </Box>
    </Box>
  );
}

// Uma situação da fila: o nome, quantos e quanto em aberto, e quantos parados.
function LinhaDaFila({ linha, alerta, onAbrir }: { linha: PedidosEmAberto; alerta?: Alerta; onAbrir: (a: Alerta) => void }) {
  const parados = linha.alem_do_prazo;
  const grave = linha.situacao === 'erro' || linha.situacao === 'a_faturar';
  const prazo =
    linha.prazo_dias_uteis == null ? '' : ` há ${quantidade(linha.prazo_dias_uteis)}+ ${linha.prazo_dias_uteis === 1 ? 'dia útil' : 'dias úteis'}`;
  const dica = [
    linha.descricao,
    linha.com_entrega_prevista ? `${plural(linha.com_entrega_prevista, 'pedido tem', 'pedidos têm')} entrega ainda por vir e não conta como parado.` : '',
    linha.antigos_sem_nota?.pedidos ? 'Os antigos sem nota ficam fora desta conta, na linha de baixo.' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Linha alerta={alerta} onAbrir={onAbrir} title={dica}>
      <Box flex="1" minW={0}>
        <Box fontSize="14px" lineHeight="20px" fontWeight={600}>
          {linha.rotulo}
        </Box>
        <Box fontSize="12px" lineHeight="17px" mt="1px">
          {parados.pedidos > 0 ? (
            <Box as="span" fontWeight={600} color={grave ? 'var(--pa-critico)' : 'var(--pa-atencao)'}>
              {linha.prazo_dias_uteis == null
                ? `${plural(parados.pedidos, 'pede', 'pedem')} correção`
                : `${plural(parados.pedidos, 'parado', 'parados')}${prazo}`}{' '}
              · {reaisCurtos(parados.valor)}
            </Box>
          ) : (
            <Box as="span" color="var(--pa-sobe)" fontWeight={600}>
              nenhum parado
            </Box>
          )}
        </Box>
      </Box>
      <Box textAlign="right" flexShrink={0}>
        <Box fontSize="15px" lineHeight="20px" fontWeight={700}>
          {quantidade(linha.pedidos)}
        </Box>
        <Box fontSize="12px" lineHeight="17px" color="var(--pa-tinta-3)" title={reais(linha.valor)}>
          {reaisCurtos(linha.valor)}
        </Box>
      </Box>
    </Linha>
  );
}

// Uma linha que não entra na conta da situação acima: os antigos sem nota, que
// abrem o aviso de limpeza do ERP.
function LinhaAParte({
  rotulo,
  detalhe,
  dica,
  numeros,
  alerta,
  onAbrir,
}: {
  rotulo: string;
  detalhe: string;
  dica: string;
  numeros: { pedidos: number; valor: number };
  alerta?: Alerta;
  onAbrir: (a: Alerta) => void;
}) {
  return (
    <Linha alerta={alerta} onAbrir={onAbrir} title={dica}>
      <Box flex="1" minW={0} pl="12px">
        <Box fontSize="13px" lineHeight="19px" fontWeight={500} color="var(--pa-tinta-2)">
          {rotulo}
        </Box>
        <Box fontSize="12px" lineHeight="17px" color="var(--pa-tinta-3)">
          {detalhe}
        </Box>
      </Box>
      <Box textAlign="right" flexShrink={0} color="var(--pa-tinta-2)">
        <Box fontSize="14px" lineHeight="20px" fontWeight={600}>
          {quantidade(numeros.pedidos)}
        </Box>
        <Box fontSize="12px" lineHeight="17px" color="var(--pa-tinta-3)" title={reais(numeros.valor)}>
          {reaisCurtos(numeros.valor)}
        </Box>
      </Box>
    </Linha>
  );
}

// "Pendências", em texto (o dono, 04/11/2026: "traga o escrito como estava,
// tira os gráficos"): o que pede ação e os pedidos em aberto EMITIDOS NO PERÍODO
// escolhido nas datas (o MCP já manda só esses), e os clientes para acompanhar.
const ddmm = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : iso);

export function AbaPendencias({
  situacao,
  periodo,
  onAbrir,
}: {
  situacao: SituacaoDeHoje;
  periodo?: { inicio: string; fim: string };
  onAbrir: (a: Alerta) => void;
}) {
  const todos = CLASSES.flatMap(([c]) => situacao.alertas[c] || []);
  const peloAlerta = (chave: string | null) => (chave ? todos.find((a) => a.chave === chave) : undefined);
  const emitidos = periodo ? `emitidos de ${ddmm(periodo.inicio)} a ${ddmm(periodo.fim)}` : 'de qualquer data';

  return (
    <Flex direction="column" sx={{ gap: '20px' }}>
      <LinhaDoPeriodo>
        Pedidos em aberto <b>{emitidos}</b>
      </LinhaDoPeriodo>
      <Grade alignItems="stretch">
        <PedeAcao
          gridColumn={{ lg: 'span 7' }}
          titulo="Pede sua ação"
          alertas={situacao.alertas}
          fora={situacao.alertas_fora_da_lista}
          resolvidos={situacao.resolvidos_recentes}
          vazio="Nada parado nem fora do ciclo."
          onAbrir={onAbrir}
        />
        <Cartao gridColumn={{ lg: 'span 5' }} overflow="hidden">
          <TituloDoCartao
            titulo="Pedidos em aberto"
            extra={
              <Box fontSize="12px" color="var(--pa-tinta-3)" whiteSpace="nowrap">
                {emitidos}
              </Box>
            }
            explicacao="Pedidos que ainda esperam algo, emitidos no período escolhido nas datas. Cada situação está com uma pessoa diferente e não se soma às outras. Parado é o que passou do prazo, em dias úteis desde a emissão; pedido a faturar com entrega ainda por vir não está parado."
          />
          {situacao.pedidos_em_aberto.map((linha) => (
            <Fragment key={linha.situacao}>
              <LinhaDaFila linha={linha} alerta={peloAlerta(linha.chave_do_alerta)} onAbrir={onAbrir} />
              {!!linha.antigos_sem_nota?.pedidos && (
                <LinhaAParte
                  rotulo="Antigos sem nota no sistema"
                  detalhe={`mais de ${linha.antigos_sem_nota.mais_de_dias} dias: conferir e encerrar no ERP`}
                  dica="Mais de 98% dos pedidos recebem a nota em até 5 dias úteis. Sem nota há mais de 30 dias, o pedido costuma ter sido encerrado ou refeito no ERP sem aviso ao sistema: não é faturamento atrasado."
                  numeros={linha.antigos_sem_nota}
                  alerta={peloAlerta(linha.chave_dos_antigos ?? null)}
                  onAbrir={onAbrir}
                />
              )}
            </Fragment>
          ))}
        </Cartao>
      </Grade>

      {situacao.clientes_para_acompanhar.length > 0 && (
        <Cartao overflow="hidden">
          <TituloDoCartao titulo="Clientes para acompanhar" explicacao="Pelo histórico de compra de cada cliente, não pelo período escolhido." />
          {situacao.clientes_para_acompanhar.map((c) => (
            <Linha key={c.nome} alerta={peloAlerta(c.chave_do_alerta) || undefined} onAbrir={onAbrir} title={c.descricao}>
              <Box flex="1" minW={0}>
                <Box fontSize="14px" lineHeight="20px" fontWeight={600}>
                  {c.rotulo}
                </Box>
                <Box fontSize="12px" lineHeight="17px" color="var(--pa-tinta-3)" noOfLines={1}>
                  {c.descricao}
                </Box>
              </Box>
              <Box fontSize="18px" fontWeight={700} flexShrink={0}>
                {quantidade(c.clientes)}
              </Box>
            </Linha>
          ))}
        </Cartao>
      )}
    </Flex>
  );
}
